<template>
  <div>
    <v-dialog
      :model-value="modelValue"
      max-width="720"
      @update:model-value="$emit('update:modelValue', $event)"
    >
      <v-card class="d-flex flex-column" max-height="90vh">
        <v-card-title class="d-flex align-center justify-space-between pa-4 flex-shrink-0">
          <span>Start with MCP</span>
          <v-btn icon variant="text" @click="$emit('update:modelValue', false)">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-divider class="flex-shrink-0" />

        <v-card-text class="pa-4 flex-grow-1 overflow-y-auto">
          <p class="text-body-2 mb-4">
            Build your domain model with an AI agent. Connect Qlerify to your AI tool via MCP
            and install the companion skills — then describe your domain or point the agent at your existing code.
          </p>

          <p class="text-body-2 font-weight-medium mb-3">
            Where are you starting from?
          </p>

          <div class="d-flex ga-3 mb-5">
            <v-card
              class="path-card flex-grow-1 pa-4"
              role="button"
              tabindex="0"
              :variant="selectedPath === 'idea' ? 'tonal' : 'outlined'"
              :color="selectedPath === 'idea' ? 'primary' : undefined"
              :aria-pressed="selectedPath === 'idea'"
              @click="selectedPath = 'idea'"
              @keydown.enter.prevent="selectedPath = 'idea'"
              @keydown.space.prevent="selectedPath = 'idea'"
            >
              <v-icon size="32" class="mb-2">
                mdi-lightbulb-on-outline
              </v-icon>
              <p class="font-weight-medium mb-1">
                From an idea
              </p>
              <p class="text-body-2 text-medium-emphasis">
                Describe your domain, let the agent model it, then generate code from it.
              </p>
            </v-card>

            <v-card
              class="path-card flex-grow-1 pa-4"
              role="button"
              tabindex="0"
              :variant="selectedPath === 'existing' ? 'tonal' : 'outlined'"
              :color="selectedPath === 'existing' ? 'primary' : undefined"
              :aria-pressed="selectedPath === 'existing'"
              @click="selectedPath = 'existing'"
              @keydown.enter.prevent="selectedPath = 'existing'"
              @keydown.space.prevent="selectedPath = 'existing'"
            >
              <v-icon size="32" class="mb-2">
                mdi-folder-search-outline
              </v-icon>
              <p class="font-weight-medium mb-1">
                Existing codebase
              </p>
              <p class="text-body-2 text-medium-emphasis">
                Extract the model from your repo, iterate, and keep code and model in sync.
              </p>
            </v-card>
          </div>

          <template v-if="selectedPath">
            <p class="text-body-2 font-weight-medium mb-3">
              Next: 3 steps
            </p>

            <div class="step-row">
              <span class="step-number">
                1.
              </span>
              <div class="step-content">
                <p class="text-body-2 mb-2">
                  Set up MCP and the companion skills in your AI agent.
                </p>
                <v-btn size="small" variant="outlined" @click="showSetupDialog = true">
                  <v-icon size="14" class="mr-1">
                    mdi-open-in-new
                  </v-icon>
                  View setup instructions
                </v-btn>
              </div>
            </div>

            <div class="step-row">
              <span class="step-number">
                2.
              </span>
              <div class="step-content">
                <p class="text-body-2 mb-0">
                  Open your AI agent (Claude Code, Cursor, Claude Desktop, etc.).
                </p>
              </div>
            </div>

            <div class="step-row">
              <span class="step-number">
                3.
              </span>
              <div class="step-content">
                <p class="text-body-2 mb-0">
                  {{ pathConfig.step3 }}
                </p>
              </div>
            </div>

            <p class="text-body-2 font-weight-medium mt-4 mb-2">
              {{ pathConfig.promptLabel }}
            </p>
            <div class="code-block-wrapper">
              <pre class="code-block">{{ pathConfig.prompt }}</pre>
              <v-btn class="copy-btn" @click="copyToClipboard(pathConfig.prompt)">
                <v-icon size="14">
                  mdi-content-copy
                </v-icon>
              </v-btn>
            </div>

            <template v-if="pathConfig.fallback">
              <p class="text-body-2 text-medium-emphasis mt-3 mb-2">
                {{ pathConfig.fallback.label }}
              </p>
              <div class="code-block-wrapper">
                <pre class="code-block">{{ pathConfig.fallback.prompt }}</pre>
                <v-btn class="copy-btn" @click="copyToClipboard(pathConfig.fallback.prompt)">
                  <v-icon size="14">
                    mdi-content-copy
                  </v-icon>
                </v-btn>
              </div>
            </template>
          </template>
        </v-card-text>

        <v-divider class="flex-shrink-0" />

        <v-card-actions class="pa-4 flex-shrink-0">
          <v-spacer />
          <v-btn variant="outlined" @click="$emit('update:modelValue', false)">
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <mcp-setup-dialog v-model="showSetupDialog" />
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useStore } from 'vuex'
import McpSetupDialog from '@/components/McpSetupDialog.vue'

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  }
})

defineEmits(['update:modelValue'])

const store = useStore()

const selectedPath = ref(null)
const showSetupDialog = ref(false)

const PATH_CONFIG = {
  idea: {
    step3: 'Paste the prompt below into your AI agent. The more detail you share about your domain, the better the resulting workflow will be.',
    promptLabel: 'Suggested prompt',
    prompt: 'I want to model a [your domain — e.g. hotel bookings] in Qlerify. Create a workflow with the domain events, commands, entities, and bounded contexts. Walk me through it as you go.'
  },
  existing: {
    step3: 'Run the workflow-creation skill. It will plan the aggregates from your code, ask for your approval, then build the model in Qlerify.',
    promptLabel: 'In Claude Code, run',
    prompt: '/mcp-companion:workflow-creation',
    fallback: {
      label: 'If your agent doesn\'t support this command syntax, paste this prompt instead',
      prompt: 'Create a workflow in Qlerify from this codebase.'
    }
  }
}

const pathConfig = computed(() => PATH_CONFIG[selectedPath.value] || {})

const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text)
    store.commit('snackbar/showMessage', {
      content: 'Copied to clipboard',
      color: 'green',
      timeout: 1500
    }, { root: true })
  } catch {
    store.commit('snackbar/showMessage', {
      content: 'Failed to copy',
      color: 'red',
      timeout: 3000
    }, { root: true })
  }
}

watch(() => props.modelValue, (open) => {
  if (!open) {
    selectedPath.value = null
    showSetupDialog.value = false
  }
})
</script>

<style scoped>
.path-card {
  cursor: pointer;
  transition: border-color 0.15s ease, background-color 0.15s ease;
}

.path-card:hover {
  border-color: rgb(var(--v-theme-primary));
}

.step-row {
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
  align-items: baseline;
}

.step-number {
  flex-shrink: 0;
  font-weight: 500;
  font-size: 0.875rem;
  line-height: 1.25rem;
  min-width: 18px;
}

.step-content {
  flex: 1;
}

.step-content > p:first-child {
  margin-top: 0;
  line-height: 1.25rem;
}

.code-block-wrapper {
  position: relative;
}

.code-block {
  font-family: 'Courier New', Courier, monospace;
  font-size: 13px;
  background-color: #1e1e1e;
  color: #d4d4d4;
  padding: 16px 48px 16px 16px;
  border-radius: 6px;
  overflow-x: auto;
  white-space: pre-wrap;
  word-break: break-word;
  margin: 0;
  line-height: 1.5;
}

.copy-btn {
  position: absolute;
  top: 8px;
  right: 8px;
  min-width: 28px !important;
  width: 28px;
  height: 28px;
  padding: 0 !important;
  background-color: rgba(255, 255, 255, 0.1) !important;
  color: #d4d4d4 !important;
}

.copy-btn:hover {
  background-color: rgba(255, 255, 255, 0.2) !important;
}
</style>
